// Comprehensive shapes data for the editor
// Organized by categories for wedding card design and general graphics
//
// NOTE: This catalog is intentionally limited to shapes that render FAITHFULLY
// in BOTH the editor and the video_worker export pipeline. The worker
// rasterizes an SVG path by filling each subpath independently (no hole
// subtraction / even-odd fill, and open stroke-only subpaths get filled as
// polygons). Shapes that depend on holes, multiple overlapping subpaths, arcs
// forming rings, or open stroke-only outlines looked correct in the editor but
// rendered wrong (or empty) in exports, so they were removed from the panel.
// The engines still RENDER those legacy shapeTypes if an older template uses
// them; they are simply no longer offered here.

export const SHAPE_CATEGORIES = {
  BASIC: 'basic',
  CALLOUTS: 'callouts',
  FLOWCHART: 'flowchart',
  BANNERS: 'banners',
  GEOMETRIC: 'geometric',
  DECORATIVE: 'decorative'
};

// B. Callout Shapes (single closed subpath — render faithfully)
export const CALLOUT_SHAPES = [
  {
    id: 'callout-rounded',
    name: 'Rounded Callout',
    shapeType: 'callout-rounded',
    category: SHAPE_CATEGORIES.CALLOUTS,
    svgPath: 'M15,15 Q15,10 20,10 L80,10 Q85,10 85,15 L85,55 Q85,60 80,60 L50,60 L40,75 L45,60 L20,60 Q15,60 15,55 Z',
    viewBox: '0 0 100 100'
  },
  {
    id: 'callout-rectangular',
    name: 'Rectangular Callout',
    shapeType: 'callout-rectangular',
    category: SHAPE_CATEGORIES.CALLOUTS,
    svgPath: 'M15,15 L85,15 L85,55 L50,55 L40,70 L45,55 L15,55 Z',
    viewBox: '0 0 100 100'
  }
];

// C. Flow Chart Shapes (single closed subpath — render faithfully)
export const FLOWCHART_SHAPES = [
  {
    id: 'flowchart-process',
    name: 'Process',
    shapeType: 'flowchart-process',
    category: SHAPE_CATEGORIES.FLOWCHART,
    svgPath: 'M15,25 L85,25 L85,75 L15,75 Z',
    viewBox: '0 0 100 100'
  },
  {
    id: 'flowchart-decision',
    name: 'Decision',
    shapeType: 'flowchart-decision',
    category: SHAPE_CATEGORIES.FLOWCHART,
    svgPath: 'M50,15 L85,50 L50,85 L15,50 Z',
    viewBox: '0 0 100 100'
  },
  {
    id: 'flowchart-start-end',
    name: 'Start/End',
    shapeType: 'flowchart-start-end',
    category: SHAPE_CATEGORIES.FLOWCHART,
    svgPath: 'M25,25 Q15,25 15,50 Q15,75 25,75 L75,75 Q85,75 85,50 Q85,25 75,25 Z',
    viewBox: '0 0 100 100'
  },
  {
    id: 'flowchart-document',
    name: 'Document',
    shapeType: 'flowchart-document',
    category: SHAPE_CATEGORIES.FLOWCHART,
    svgPath: 'M15,15 L85,15 L85,70 Q70,80 50,70 Q30,80 15,70 Z',
    viewBox: '0 0 100 100'
  }
];

// D. Banner Shapes (single closed subpath — render faithfully)
export const BANNER_SHAPES = [
  {
    id: 'banner-ribbon',
    name: 'Ribbon Banner',
    shapeType: 'banner-ribbon',
    category: SHAPE_CATEGORIES.BANNERS,
    svgPath: 'M10,30 L80,30 L90,50 L80,70 L10,70 L20,50 Z',
    viewBox: '0 0 100 100'
  },
  {
    id: 'banner-flag',
    name: 'Flag Banner',
    shapeType: 'banner-flag',
    category: SHAPE_CATEGORIES.BANNERS,
    svgPath: 'M15,20 L85,20 L75,50 L85,80 L15,80 Z',
    viewBox: '0 0 100 100'
  },
  {
    id: 'banner-scroll',
    name: 'Scroll Banner',
    shapeType: 'banner-scroll',
    category: SHAPE_CATEGORIES.BANNERS,
    svgPath: 'M20,25 Q15,20 15,30 L15,70 Q15,80 20,75 L80,75 Q85,80 85,70 L85,30 Q85,20 80,25 Z',
    viewBox: '0 0 100 100'
  }
];

// E. Geometric Shapes (single closed subpath — render faithfully)
export const GEOMETRIC_SHAPES = [
  {
    id: 'rhomboid',
    name: 'Rhomboid',
    shapeType: 'rhomboid',
    category: SHAPE_CATEGORIES.GEOMETRIC,
    svgPath: 'M25,20 L85,20 L75,80 L15,80 Z',
    viewBox: '0 0 100 100'
  },
  {
    id: 'kite',
    name: 'Kite',
    shapeType: 'kite',
    category: SHAPE_CATEGORIES.GEOMETRIC,
    svgPath: 'M50,10 L75,50 L50,90 L25,50 Z',
    viewBox: '0 0 100 100'
  },
  {
    id: 'diamond',
    name: 'Diamond',
    shapeType: 'diamond',
    category: SHAPE_CATEGORIES.GEOMETRIC,
    svgPath: 'M50,15 L75,50 L50,85 L25,50 Z',
    viewBox: '0 0 100 100'
  }
];

// F. Decorative Shapes (single closed subpath — render faithfully)
export const DECORATIVE_SHAPES = [
  {
    id: 'scalloped-edge',
    name: 'Scalloped Edge',
    shapeType: 'scalloped-edge',
    category: SHAPE_CATEGORIES.DECORATIVE,
    svgPath: 'M10,20 Q20,10 30,20 Q40,10 50,20 Q60,10 70,20 Q80,10 90,20 L90,80 L10,80 Z',
    viewBox: '0 0 100 100'
  },
  {
    id: 'paisley',
    name: 'Paisley',
    shapeType: 'paisley',
    category: SHAPE_CATEGORIES.DECORATIVE,
    svgPath: 'M30,70 Q20,50 30,30 Q40,20 50,30 Q60,40 50,50 Q55,60 50,70 Q40,80 30,70',
    viewBox: '0 0 100 100'
  }
];

// Combine all shapes for easy access
export const ALL_SHAPES = [
  ...CALLOUT_SHAPES,
  ...FLOWCHART_SHAPES,
  ...BANNER_SHAPES,
  ...GEOMETRIC_SHAPES,
  ...DECORATIVE_SHAPES
];

// Get shapes by category
export const getShapesByCategory = (category) => {
  return ALL_SHAPES.filter(shape => shape.category === category);
};

// Get all categories
export const getAllCategories = () => {
  return Object.values(SHAPE_CATEGORIES);
};
